import type { AccountLimitsVersion, MeResponse, UpdateAccountLimitsRequest } from '../../api';

import { isHalfTypedDecimal, lacksLeadingDigit, readDecimal } from './exact-decimal';

/** The account's current daily-loss limit, in basis points of the capital (500 = 5 %). */
export interface CurrentDailyLimit {
  readonly bps: number;
}

/**
 * What the limits card knows about the account. The limit is account-wide (FXM-02 §4); it comes
 * from `accountLimits` of `GET …/settings` (C1 R6), together with its `version`.
 * `unavailable`: the limit cannot be read, so the switch is not shown as on or off.
 */
export type LimitsAccount =
  | {
      readonly kind: 'unavailable';
      /**
       * A C2 reason code, except `DAILY_LOSS_NOT_AVAILABLE`: a reachable account with no reading
       * of today's loss (M1 R20, M2 AC20), which is not a failure of the account and has its own text.
       */
      readonly reasonCode:
        | 'TRADING_ACCOUNT_NOT_CONNECTED'
        | 'TRADING_ACCOUNT_UNREACHABLE'
        | 'DAILY_LOSS_NOT_AVAILABLE';
    }
  | {
      readonly kind: 'available';
      /** `null`: no limit set (off, I7). */
      readonly current: CurrentDailyLimit | null;
      /**
       * `version` of the limits the trader sees, sent back as `expectedVersion` (B-D16); `null`
       * when no limit was ever saved. A limit turned off keeps its version.
       */
      readonly version: number | null;
    };

/** The limits card as the trader fills it. `amount` is a new limit typed in % of the capital. */
export interface DailyLimitDraft {
  readonly enabled: boolean;
  readonly amount: string;
}

/** Result of {@link validateDailyLimitDraft}. */
export type DailyLimitValidation =
  | { readonly ok: true; readonly change: 'unchanged' }
  | { readonly ok: true; readonly change: 'set'; readonly body: UpdateAccountLimitsRequest }
  | {
      readonly ok: false;
      readonly problem: 'required' | 'incomplete' | 'invalid';
      /** An incomplete amount with no digit before its separator (",5"): the card says so. */
      readonly leadingDigit?: true;
    };

/** The card leaves the account limit as it is: no `PUT /account/limits`. */
const unchanged: DailyLimitValidation = { ok: true, change: 'unchanged' };

/** Lowest and highest limit, in basis points (C1 R6: 0.01 % to 100 %). */
const MIN_BPS = 1;
const MAX_BPS = 10_000;

/** A percentage has at most two decimals: one basis point is 0.01 %. */
const PERCENT_DECIMALS = 2;

/**
 * The limits card's account state. Whether the limit can be read comes from `GET /me` (no
 * account, or an unreachable one, hides the switch); the limit and its version come from
 * `accountLimits` of `GET …/settings` (C1 R6, M2 R12).
 */
export function limitsAccountFromMe(
  me: MeResponse,
  accountLimits: AccountLimitsVersion | null,
): LimitsAccount {
  if (me.tradingAccount === null) {
    return { kind: 'unavailable', reasonCode: 'TRADING_ACCOUNT_NOT_CONNECTED' };
  }
  // "Not responding" only when the account itself is (M2 AC20): a reachable account with no
  // reading of today's loss gets its own wording.
  if (me.tradingAccount.connection === 'unreachable' || me.tradingAccount.equity === null) {
    return { kind: 'unavailable', reasonCode: 'TRADING_ACCOUNT_UNREACHABLE' };
  }
  if (me.dailyLoss === null) {
    return { kind: 'unavailable', reasonCode: 'DAILY_LOSS_NOT_AVAILABLE' };
  }
  return accountLimits === null
    ? { kind: 'available', current: null, version: null }
    : availableFrom(accountLimits);
}

function availableFrom(saved: AccountLimitsVersion): LimitsAccount {
  return {
    kind: 'available',
    current: saved.maxDailyLossBps === null ? null : { bps: saved.maxDailyLossBps },
    version: saved.version,
  };
}

/** On only when a limit is already saved (I7: a switch is never pre-checked otherwise). */
export function initialDailyLimitDraft(account: LimitsAccount): DailyLimitDraft {
  return { enabled: account.kind === 'available' && account.current !== null, amount: '' };
}

/**
 * The card's account after a successful `PUT /account/limits`: the saved limit and its new
 * version become the current ones, so the next save compares with them (and does not send the
 * same limit again, nor a stale `expectedVersion`).
 */
export function accountAfterSave(
  account: LimitsAccount,
  saved: AccountLimitsVersion,
): LimitsAccount {
  if (account.kind === 'unavailable') return account;
  return availableFrom(saved);
}

/**
 * Turns the card into a `PUT /account/limits` body, or `unchanged` when the trader did not touch
 * it: the limit is account-wide, so saving one channel never rewrites it by accident (M2 R12).
 *
 * The trader types a percentage of the capital with at most two decimals; the body carries it as
 * integer basis points (5 % is 500, C1 R6). The text is read digit by digit with
 * {@link readDecimal}, never through a float, so 5.55 is exactly 555. Nothing is rounded: a third
 * decimal, 0 or more than 100 % is an error (I7). `expectedVersion` is the version the card shows
 * (B-D16).
 */
export function validateDailyLimitDraft(
  draft: DailyLimitDraft,
  account: LimitsAccount,
): DailyLimitValidation {
  if (account.kind === 'unavailable') return unchanged;
  const wasEnabled = account.current !== null;
  if (!draft.enabled) {
    return wasEnabled
      ? {
          ok: true,
          change: 'set',
          body: { maxDailyLossBps: null, expectedVersion: account.version },
        }
      : unchanged;
  }
  if (draft.amount.trim() === '') {
    return wasEnabled ? unchanged : { ok: false, problem: 'required' };
  }
  if (lacksLeadingDigit(draft.amount)) {
    return { ok: false, problem: 'incomplete', leadingDigit: true };
  }
  if (isHalfTypedDecimal(draft.amount)) return { ok: false, problem: 'incomplete' };
  const read = readDecimal(draft.amount);
  if (read === null || read.value.scale > PERCENT_DECIMALS)
    return { ok: false, problem: 'invalid' };
  const bps = read.value.units * 10n ** BigInt(PERCENT_DECIMALS - read.value.scale);
  if (bps < BigInt(MIN_BPS) || bps > BigInt(MAX_BPS)) return { ok: false, problem: 'invalid' };
  return {
    ok: true,
    change: 'set',
    body: { maxDailyLossBps: Number(bps), expectedVersion: account.version },
  };
}
